/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.1.3): util/backdrop.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/master/LICENSE)
 * --------------------------------------------------------------------------
 */

import EventHandler from 'bootstrap/js/src/dom/event-handler';
import { typeCheckConfig } from 'bootstrap/js/src/util';
import { getElement } from '../util/dom';
import { animController } from '../util/animate-css';
import Deferred from '../util/deferred-promise';

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'backdrop';
const CLASS_NAME_FADE = 'fade';
const CLASS_NAME_SHOW = 'show';

export const EVENT_MOUSEDOWN = `mousedown.bs.${NAME}`;

export const Default = {
	className: 'modal-backdrop',
	isVisible: true, // if false, we use the backdrop helper without adding any element to the dom
	isAnimated: false,
	rootElement: 'body', // give the choice to place backdrop under different elements
	clickCallback: null
};

export const DefaultType = {
	className: 'string',
	isVisible: 'boolean',
	isAnimated: 'boolean',
	rootElement: '(element|string)',
	clickCallback: '(function|null)'
};

export default class Backdrop {

	// ==============
	// Props
	// ==============

	// ==============
	// Public Methods
	// ==============

	constructor(config) {
		this._config = this._getConfig(config);
		this._isAppended = false;
		this._element = null;
		/** @type {import('../util/deferred-promise').Deferred} */
		this._ac = null;
	}

	show(callback) {

		// End current transition
		this.endAnim();

		if (!this._config.isVisible) {
			callback && callback();
			return void 0;
		}

		this._append();

		this.startAnim().done(callback);

		// Add the show class after the 'fade' transition, because otherwise it doesn't animate
		// Note: Seems that the order of classes on the element matters, so we must have the classes in this order:
		// 'fade show' -> 1st is the transition class, 2nd is the destination opacity class
		this._getElement().classList.add(CLASS_NAME_SHOW);

		return this;
	}

	hide(callback) {

		// End current transition
		this.endAnim();

		if (!this._isAppended || !this._config.isVisible) {
			callback && callback();
			return void 0;
		}

		this._getElement().classList.remove(CLASS_NAME_SHOW);

		this.startAnim().done(() => {
			this.dispose();
		}, callback);

		return this;
	}

	getAnim() {
		if (this._ac) {
			return this._ac;
		}
		return Deferred.resolve(this._getElement());
	}

	startAnim() {
		this._ac = animController({
			element: this._getElement(),
			classList: [CLASS_NAME_FADE],
			mode: 'add_remove',
			waitForEnd: this._config.isAnimated,
		}).always(_ => {
			this._ac = null;
		});
		return this._ac;
	}

	endAnim() {
		if (this._ac) {
			this._ac.resolve(this._element);
		}

		return this;
	}

	dispose() {
		if (!this._isAppended) {
			return;
		}

		// End current transition
		this.endAnim();

		EventHandler.off(this._element, EVENT_MOUSEDOWN);

		this._element.remove();
		this._isAppended = false;
	}

	// ==========================
	// Private/Protected Methods
	// ==========================

	_getElement() {
		if (!this._element) {
			const backdrop = document.createElement('div');
			backdrop.className = this._config.className;
			this._element = backdrop;
		}

		return this._element;
	}

	_getConfig(config) {
		config = {
			...Default,
			...(typeof config === 'object' ? config : {})
		};

		// use getElement() with the default "body" to get a fresh Element on each instantiation
		config.rootElement = document.getElementById("mmenu_offcanvas") ? getElement('#mmenu_offcanvas') : getElement(config.rootElement);
		typeCheckConfig(NAME, config, DefaultType);
		return config;
	}

	_append() {
		if (this._isAppended) {
			return this;
		}

		this._config.rootElement.append(this._getElement());

		EventHandler.on(this._getElement(), EVENT_MOUSEDOWN, () => {
			this._config.clickCallback && this._config.clickCallback();
		});

		this._isAppended = true;

		return this;
	}
}

// @ts-ignore
// window.Backdrop = Backdrop;
